import React, { useCallback, useEffect, useState } from "react";
import "../../App.css";
import "../../profile.css";

import { ArrowLeft, X, Download } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import {
  GoogleMap,
  Polyline,
  Marker,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";

import polyline from "@mapbox/polyline";

import service from "../../services/service";
import endpoints from "../../services/endpoints";
import {
  GREEN_MILESTONE_ICON,
  RED_CHECKPOINT_ICON,
  buildRouteSegments,
  decodeRoutePolyline,
  flattenRouteSegments,
  isConnectorPoint,
  isSameLatLng,
  sortRoutePoints,
  toLatLng,
} from "../../utils/routeMapHelpers";

const mapContainerStyle = {
  width: "100%",
  height: "420px",
};

const startEndOverlayStyle = {
  transform: "translate(-50%, -65px)",
  background: "#fff",
  border: "2px solid #ef4444",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "13px",
  fontWeight: "600",
  color: "#222",
  boxShadow: "0 3px 10px rgba(0,0,0,.25)",
  width: "100px",
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const milestoneOverlayStyle = {
  ...startEndOverlayStyle,
  border: "2px solid #22c55e",
};

const MissionDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [mission, setMission] = useState(null);

  const [path, setPath] = useState([]);
  const [completedPath, setCompletedPath] = useState([]);
  const [remainingPath, setRemainingPath] = useState([]);

  // BIB Modal State
  const [showBibModal, setShowBibModal] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const fetchMissionDetails = async () => {
    try {
      setLoading(true);

      const response = await service.get(endpoints.profile.missionDetails(id));

      const data = response?.data?.data;

      setMission(data);

      const sortedRoutePoints = sortRoutePoints(data?.routePoints);
      const decoded = decodeRoutePolyline(data?.routePolyline, polyline);
      const segments = buildRouteSegments(sortedRoutePoints, decoded);
      const flattened = flattenRouteSegments(segments);

      const routePath =
        flattened.length > 1
          ? flattened
          : decoded.length > 1
            ? decoded
            : [];

      setPath(routePath);

      const progress = data?.progressPercentage || 0;
      const splitIndex = Math.floor((routePath.length * progress) / 100);

      setCompletedPath(routePath.slice(0, splitIndex));
      setRemainingPath(routePath.slice(splitIndex));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMissionDetails();
  }, [id]);

  const onLoad = useCallback(
    (map) => {
      if (path.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();

        path.forEach((point) => bounds.extend(point));

        map.fitBounds(bounds);
      }
    },
    [path],
  );

  // Helper function to handle cross-origin image download
  const handleDownloadBib = async () => {
    const imageUrl = mission?.starterBibImgUrl;

    if (!imageUrl) return;

    try {
      const response = await fetch(imageUrl, {
        mode: "cors",
        cache: "no-store",
      });

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `Starter_BIB_${mission?.starterBib || "download"}.png`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading BIB image:", error);

      // Fallback: direct download by opening the image
      const link = document.createElement("a");
      link.href = imageUrl;
      link.target = "_blank";
      link.download = "Starter_BIB.png";
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="mission-details-page">
        <h3>Loading mission...</h3>
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="mission-details-page">
        <h3>Mission not found</h3>
      </div>
    );
  }

  const sortedRoutePoints = sortRoutePoints(mission?.routePoints);

  const startPosition =
    Number.isFinite(Number(mission.startPointLatitude)) &&
    Number.isFinite(Number(mission.startPointLongitude)) &&
    (Number(mission.startPointLatitude) !== 0 ||
      Number(mission.startPointLongitude) !== 0)
      ? {
          lat: Number(mission.startPointLatitude),
          lng: Number(mission.startPointLongitude),
        }
      : path[0] || null;

  const endPosition =
    Number.isFinite(Number(mission.endPointLatitude)) &&
    Number.isFinite(Number(mission.endPointLongitude)) &&
    (Number(mission.endPointLatitude) !== 0 ||
      Number(mission.endPointLongitude) !== 0)
      ? {
          lat: Number(mission.endPointLatitude),
          lng: Number(mission.endPointLongitude),
        }
      : path[path.length - 1] || null;

  const mapMilestones = Array.isArray(mission.milestoneDetails)
    ? mission.milestoneDetails
    : [];

  const milestonePositions = mapMilestones
    .map((item) => {
      const ownPosition = toLatLng(item);
      if (!ownPosition) return null;

      if (item?.title) {
        const byTitle = sortedRoutePoints.find(
          (point) =>
            point.title &&
            !isConnectorPoint(point) &&
            point.title === item.title,
        );
        const titlePosition = toLatLng(byTitle);
        if (titlePosition) {
          return { item, position: titlePosition };
        }
      }

      return { item, position: ownPosition };
    })
    .filter(Boolean);

  const checkpointMarkers = sortedRoutePoints.filter((point) => {
    if (isConnectorPoint(point)) return false;

    const position = toLatLng(point);
    if (!position) return false;

    if (startPosition && isSameLatLng(position, startPosition)) return false;
    if (endPosition && isSameLatLng(position, endPosition)) return false;

    const matchesMilestone = milestonePositions.some(({ position: milestonePos }) =>
      isSameLatLng(position, milestonePos),
    );
    if (matchesMilestone) return false;

    return true;
  });

  return (
    <div className="mission-details-page">
      {/* HEADER */}

      <div className="mission-details-top">
        <div className="mission-details-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={22} />
          <h2>Mission Details</h2>
        </div>

        <div className="mission-details-actions">
          <span
            className={`mission-details-status ${
              mission.challengeStatus === "IN_PROGRESS"
                ? "mission-started"
                : mission.challengeStatus === "PAUSED"
                  ? "mission-paused"
                  : "mission-completed"
            }`}
          >
            {mission.challengeStatus}
          </span>

          <button
            className="mission-bib-btn"
            onClick={() => setShowBibModal(true)}
          >
            View BIB
          </button>

          <button
            className="mission-distance-btn"
            onClick={() =>
              navigate(
                `/all-challenges/add-distance/${mission.userChallengeId}`,
              )
            }
          >
            Add Distance Covered
          </button>
        </div>
      </div>

      {/* MAP */}

      <div className="mission-map-card">
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={
              path[0] ||
              startPosition || {
                lat: 28.6139,
                lng: 77.209,
              }
            }
            zoom={8}
            onLoad={onLoad}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            {/* COMPLETED ROUTE */}
            {completedPath.length > 1 && (
              <Polyline
                path={completedPath}
                options={{
                  strokeColor: "#ff2b2b",
                  strokeWeight: 6,
                  zIndex: 2,
                }}
              />
            )}

            {/* REMAINING ROUTE */}
            {remainingPath.length > 1 && (
              <Polyline
                path={remainingPath}
                options={{
                  strokeColor: "#ffffff",
                  strokeWeight: 6,
                  zIndex: 1,
                }}
              />
            )}

            {/* Checkpoint / route points — RED */}
            {checkpointMarkers.map((point) => {
              const position = toLatLng(point);
              return (
                <Marker
                  key={
                    point.id ??
                    `route-${point.pointOrder}-${position.lat}-${position.lng}`
                  }
                  position={position}
                  icon={RED_CHECKPOINT_ICON}
                  title={point.title || "Checkpoint"}
                />
              );
            })}

            {/* START */}
            {startPosition && (
              <>
                <Marker position={startPosition} />

                <OverlayView
                  position={startPosition}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div style={startEndOverlayStyle}>{mission.startPoint}</div>
                </OverlayView>
              </>
            )}

            {/* END */}
            {endPosition && (
              <>
                <Marker position={endPosition} />

                <OverlayView
                  position={endPosition}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div style={startEndOverlayStyle}>{mission.endPoint}</div>
                </OverlayView>
              </>
            )}

            {/* CURRENT POSITION */}
            {completedPath.length > 0 && (
              <Marker position={completedPath[completedPath.length - 1]} />
            )}

            {/* All milestones from milestoneDetails — GREEN */}
            {milestonePositions.map(({ item, position }) => (
              <React.Fragment
                key={item.id ?? item.milestoneId ?? `${position.lat}-${position.lng}`}
              >
                <Marker position={position} icon={GREEN_MILESTONE_ICON} />

                <OverlayView
                  position={position}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div style={milestoneOverlayStyle}>{item.title}</div>
                </OverlayView>
              </React.Fragment>
            ))}
          </GoogleMap>
        )}
      </div>

      {/* STATS */}

      <div className="mission-stats-grid">
        <div className="mission-stat-card">
          <span>Current Position</span>
          <h3>{mission.currentMilestone?.title || "-"}</h3>
        </div>

        <div className="mission-stat-card">
          <span>Challenge Type</span>
          <h3>{mission.challengeType}</h3>
        </div>

        <div className="mission-stat-card">
          <span>Distance Covered</span>
          <h3>{mission.distanceCoveredInfo?.display}</h3>
        </div>

        <div className="mission-stat-card">
          <span>Distance Pending</span>
          <h3>{mission.distancePendingInfo?.display}</h3>
        </div>

        <div className="mission-stat-card">
          <span>Position</span>
          <h3>#{mission.position}</h3>
        </div>
      </div>

      {/* TEAM */}

      <div className="mission-team-section">
        <h3>Team Members ({mission.teamMembers?.length || 0})</h3>

        {mission.teamMembers?.length > 0 ? (
          <div className="mission-team-table">
            <div className="mission-team-head">
              <span>Ranking</span>
              <span>Member Name</span>
              <span>Distance Covered</span>
              <span>Start Date</span>
            </div>

            {mission.teamMembers.map((member, index) => (
              <div key={index} className="mission-team-row">
                <span>#{index + 1}</span>

                <span>{member.memberName}</span>

                <span>{member.distanceCovered} KM</span>

                <span>
                  {new Date(member.startDate).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="mission-empty-team">
            <p>{mission.teamMessage}</p>
          </div>
        )}
      </div>

      {/* SHOW BIB PREVIEW MODAL */}
      {showBibModal && (
        <div className="challenge-modal-overlay">
          <div
            className="challenge-success-modal"
            style={{ maxWidth: "420px", width: "100%", padding: "24px" }}
          >
            <button
              className="challenge-modal-close"
              onClick={() => setShowBibModal(false)}
            >
              <X size={18} />
            </button>

            <h3 style={{ marginBottom: "16px", fontSize: "20px" }}>
              Starter BIB
            </h3>

            {mission?.starterBibImgUrl ? (
              <div
                style={{
                  width: "100%",
                  borderRadius: "10px",
                  overflow: "hidden",
                  marginBottom: "20px",
                  border: "1px solid #eee",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              >
                <img
                  src={mission.starterBibImgUrl}
                  alt="Starter BIB"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              </div>
            ) : (
              <p style={{ color: "#777", margin: "20px 0" }}>
                No BIB image available.
              </p>
            )}

            <button
              className="challenge-success-btn"
              onClick={handleDownloadBib}
              disabled={!mission?.starterBibImgUrl}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
              }}
            >
              <Download size={18} />
              Download BIB
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionDetails;
